import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { generateAndSaveTemplateThumbnail } from '../lib/thumbnailGenerator';
import type { Template, TemplateCategory } from '../types';

interface TemplateState {
  templates: Template[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  searchQuery: string;
  categoryFilter: TemplateCategory | null;

  loadTemplates: () => Promise<void>;
  loadTemplate: (id: string) => Promise<Template | null>;
  saveAsTemplate: (params: {
    name: string;
    description: string;
    category: TemplateCategory;
    tags: string[];
    isPublished: boolean;
    canvasState: Record<string, unknown>;
    dynamicFields: unknown[];
    projectId?: string | null;
  }) => Promise<Template | null>;
  updateTemplate: (id: string, updates: Partial<Template>) => Promise<boolean>;
  deleteTemplate: (id: string) => Promise<boolean>;
  incrementUsage: (id: string) => Promise<void>;

  setSearchQuery: (query: string) => void;
  setCategoryFilter: (category: TemplateCategory | null) => void;
  clearError: () => void;
}

export const useTemplateStore = create<TemplateState>((set, get) => ({
  templates: [],
  isLoading: false,
  isSaving: false,
  error: null,
  searchQuery: '',
  categoryFilter: null,

  loadTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('cs_templates')
        .select('id, project_id, name, description, dynamic_fields, is_published, version, thumbnail_url, category, tags, usage_count, created_by, created_at, updated_at')
        .order('updated_at', { ascending: false });

      const { categoryFilter } = get();
      if (categoryFilter) {
        query = query.eq('category', categoryFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      set({ templates: (data || []) as Template[], isLoading: false });
    } catch {
      set({ error: 'Failed to load templates', isLoading: false });
    }
  },

  loadTemplate: async (id) => {
    try {
      const { data, error } = await supabase
        .from('cs_templates')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data as Template | null;
    } catch {
      set({ error: 'Failed to load template' });
      return null;
    }
  },

  saveAsTemplate: async ({ name, description, category, tags, isPublished, canvasState, dynamicFields, projectId }) => {
    set({ isSaving: true, error: null });
    try {
      const { data, error } = await supabase
        .from('cs_templates')
        .insert({
          project_id: projectId || null,
          name,
          description,
          canvas_state: canvasState,
          dynamic_fields: dynamicFields,
          is_published: isPublished,
          version: 1,
          category,
          tags,
        })
        .select()
        .single();

      if (error) throw error;

      let template = data as Template;

      try {
        const { useCanvasStore } = await import('./canvasStore');
        const cs = useCanvasStore.getState();
        const fc = cs.fabricCanvas;
        if (fc) {
          const thumbnailUrl = await generateAndSaveTemplateThumbnail(
            fc,
            template.id,
            cs.canvasWidth,
            cs.canvasHeight
          );
          if (thumbnailUrl) {
            template = { ...template, thumbnail_url: thumbnailUrl };
          }
        }
      } catch (thumbErr) {
        console.warn('[Template] Thumbnail generation failed (non-blocking):', thumbErr);
      }

      set((state) => ({
        templates: [template, ...state.templates],
        isSaving: false,
      }));
      return template;
    } catch {
      set({ error: 'Failed to save template', isSaving: false });
      return null;
    }
  },

  updateTemplate: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('cs_templates')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        templates: state.templates.map((t) =>
          t.id === id ? { ...t, ...updates, updated_at: new Date().toISOString() } : t
        ),
      }));
      return true;
    } catch {
      set({ error: 'Failed to update template' });
      return false;
    }
  },

  deleteTemplate: async (id) => {
    try {
      try {
        await supabase.storage
          .from('cs-assets')
          .remove([`templates/${id}.jpg`]);
      } catch (storageErr) {
        console.warn('[Template] Thumbnail cleanup skipped:', storageErr);
      }

      const { error } = await supabase
        .from('cs_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
      }));
      return true;
    } catch {
      set({ error: 'Failed to delete template' });
      return false;
    }
  },

  incrementUsage: async (id) => {
    try {
      await supabase.rpc('increment_template_usage', { template_id: id });
    } catch {
      // non-critical
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),
  setCategoryFilter: (category) => set({ categoryFilter: category }),
  clearError: () => set({ error: null }),
}));
